
import { API_BASE_URL, API_KEY } from '../../constants';
import { MaintenanceItem, Message, Priority, UpdatePayload, AuditLog, Status, Caregiver } from '../../types';
import { REVERSE_PRIORITY_MAP } from './priorityHelper';

/**
 * Fetches maintenance items for a given facility, optionally filtered by status.
 */
export const fetchMaintenanceItems = async (centreId: number, statuses: Status[]): Promise<MaintenanceItem[]> => {
  let url = `${API_BASE_URL}/base64?centre=${centreId}`;
  
  if (statuses.length > 0) {
    const statusParam = statuses.join(',');
    url += `&status=${statusParam}`;
  }
  
  const response = await fetch(url, {
    headers: {
      'x-api-key': API_KEY,
    },
  });

  if (!response.ok) {
    throw new Error(`API call failed with status: ${response.status}`);
  }

  const data = await response.json();
  return data as MaintenanceItem[];
};

/**
 * Fetches the list of caregivers/staff for a specific centre to be used in assignment dropdowns.
 * Robustly handles common API wrapper patterns and ensures fields are correctly mapped.
 */
export const fetchCaregivers = async (centreId: number): Promise<Caregiver[]> => {
  // Construct URL to target the caregivers endpoint sibling to the maintenance endpoint
  const baseUrl = API_BASE_URL.replace('/maintenance', '');
  const url = `${baseUrl}/caregivers?centre=${centreId}&role=homecare,finance,superadmin`;
  
  const response = await fetch(url, {
    headers: {
      'x-api-key': API_KEY,
    },
  });

  if (!response.ok) {
    throw new Error(`API call to fetch caregivers failed with status: ${response.status}`);
  }

  const data = await response.json();
  
  // The API might return the array directly or wrapped in a 'payload', 'data', or 'caregivers' property.
  const rawArray = Array.isArray(data) ? data : (data.payload || data.data || data.caregivers || []);

  // Explicitly map properties to the Caregiver interface to ensure the UI has what it needs.
  return rawArray.map((item: any) => {
    const firstName = item.first_name || '';
    const lastName = item.last_name || '';
    return {
      id: item.id || item.staff_id || 0,
      first_name: firstName,
      last_name: lastName,
      full_name: item.full_name || `${firstName} ${lastName}`.trim() || 'Unnamed Staff',
      // Map 'type' from API to role, falling back to other fields or 'Staff'
      role: item.type || item.role || item.role_name || 'Staff'
    };
  });
};

/**
 * Fetches all messages for a specific maintenance item.
 */
export const fetchMessagesForItem = async (maintenanceId: number): Promise<Message[]> => {
    const messageUrl = `${API_BASE_URL}/message?maint_id=${maintenanceId}`;
    const response = await fetch(messageUrl, {
        headers: {
            'x-api-key': API_KEY,
        },
    });

    if (!response.ok) {
        throw new Error(`API call to fetch messages failed with status: ${response.status}`);
    }

    const data = await response.json();
    return data as Message[];
};

/**
 * Updates a maintenance item on the server.
 */
export const updateMaintenanceItem = async (id: number, payload: UpdatePayload): Promise<MaintenanceItem> => {
  const updateUrl = `${API_BASE_URL}/${id}`;
  
  const apiPayload: { [key: string]: any } = {};
  if (payload.title) apiPayload.title = payload.title;
  if (payload.description) apiPayload.description = payload.description;
  if (payload.status) apiPayload.status = payload.status;
  if (payload.priority) {
    apiPayload.priority = REVERSE_PRIORITY_MAP[payload.priority];
  }
  if ('issues_category' in payload) {
    apiPayload.issues_category = payload.issues_category;
  }
  if (payload.assigned_to !== undefined) {
    apiPayload.assigned_to = payload.assigned_to;
  }

  const response = await fetch(updateUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': API_KEY,
    },
    body: JSON.stringify({ payload: apiPayload }),
  });

  if (!response.ok) {
    const errorData = await response.text();
    throw new Error(`API call failed with status: ${response.status}. Message: ${errorData}`);
  }

  const updatedItem = await response.json();
  return updatedItem as MaintenanceItem;
};

/**
 * Creates a new maintenance item on the server.
 */
export const createMaintenanceItem = async (title: string, description: string, priority: Priority, centreId: number, userId: string): Promise<void> => {
  const priorityApiMap: { [key in Priority]: string } = {
    [Priority.Critical]: 'P1',
    [Priority.High]: 'P2',
    [Priority.Medium]: 'P3',
    [Priority.Low]: 'P4',
  };
  
  const now = new Date();
  const createdAt = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;

  const payload = {
    title: title.trim(),
    description: description.trim(),
    priority: priorityApiMap[priority],
    centre_id: centreId,
    created_by: userId,
    created_at: createdAt,
  };

  const response = await fetch(API_BASE_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': API_KEY,
    },
    body: JSON.stringify({ payload }),
  });

  if (!response.ok) {
    const errorData = await response.text();
    throw new Error(`API call failed with status: ${response.status}. Message: ${errorData}`);
  }
};

/**
 * Adds a message to a maintenance item.
 */
export const addMessageToMaintenanceItem = async (maintenanceId: number, message: string, userId: string, imageUrl?: string): Promise<void> => {
  const now = new Date();
  const createdAt = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;

  const payload: {
    maintanance_id: number;
    message: string;
    created_by: string;
    created_at: string;
    image?: string;
  } = {
    maintanance_id: maintenanceId,
    message: message.trim(),
    created_by: userId,
    created_at: createdAt,
  };

  if (imageUrl) {
    payload.image = imageUrl;
  }

  const response = await fetch(`${API_BASE_URL}/message`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': API_KEY,
    },
    body: JSON.stringify({ payload }),
  });

  if (!response.ok) {
    const errorData = await response.text();
    throw new Error(`API call failed with status: ${response.status}. Message: ${errorData}`);
  }
};

/**
 * Uploads a base64 encoded image.
 */
export const uploadImageBase64 = async (maintenanceId: number, imageBase64: string, userId: string): Promise<void> => {
  const url = `${API_BASE_URL}/images/base64`;
  const payload = {
    f_maintenance_id: maintenanceId,
    created_by: parseInt(userId, 10) || 0,
    image_base_64: imageBase64,
  };

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': API_KEY,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.text();
    throw new Error(`API call to upload image failed with status: ${response.status}. Message: ${errorData}`);
  }
};
